'use client'

import { useState, forwardRef } from 'react'
import { CheckCircle, XCircle, Clock, Loader2, RotateCcw } from 'lucide-react'
import { cn } from '../utils/styles'

export interface ParticipationButtonProps {
  currentStatus?: 'pending' | 'confirmed' | 'declined'
  disabled?: boolean
  loading?: boolean
  onStatusChange?: (status: 'confirmed' | 'declined') => void | Promise<void>
  onAccept?: () => void | Promise<void>
  onDecline?: () => void | Promise<void>
  onReset?: () => void | Promise<void>
  className?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'outline' | 'compact'
  showStatus?: boolean
  showResetOption?: boolean
  confirmText?: string
  declineText?: string
  pendingText?: string
  acceptText?: string
  declineAlternativeText?: string
  acceptAlternativeText?: string
  resetText?: string
  loadingText?: string
  entityName?: string
  hideLabels?: boolean
  orientation?: 'horizontal' | 'vertical'
}

export const ParticipationButton = forwardRef<HTMLDivElement, ParticipationButtonProps>(({
  currentStatus = 'pending',
  disabled = false,
  loading = false,
  onStatusChange,
  onAccept,
  onDecline,
  onReset,
  className,
  size = 'md',
  variant = 'default',
  showStatus = true,
  showResetOption = false,
  confirmText = 'Confirmed',
  declineText = 'Declined',
  pendingText = 'Pending Response',
  acceptText = 'Accept',
  declineAlternativeText = 'Accept Instead',
  acceptAlternativeText = 'Decline Instead',
  resetText = 'Reset',
  loadingText = 'Updating...',
  entityName = '',
  hideLabels = false,
  orientation = 'horizontal',
  ...props
}, ref) => {
  const [isUpdating, setIsUpdating] = useState(false)

  const handleStatusUpdate = async (status: 'confirmed' | 'declined') => {
    if (disabled || isUpdating || loading) return

    setIsUpdating(true)
    try {
      if (onStatusChange) {
        await onStatusChange(status)
      } else if (status === 'confirmed' && onAccept) {
        await onAccept()
      } else if (status === 'declined' && onDecline) {
        await onDecline()
      }
    } catch (error) {
      console.error('Error updating participation status:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleReset = async () => {
    if (disabled || isUpdating || loading || !onReset) return

    setIsUpdating(true)
    try {
      await onReset()
    } catch (error) {
      console.error('Error resetting participation status:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const isLoading = loading || isUpdating

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'h-7 px-2 text-xs'
      case 'lg':
        return 'h-12 px-6 text-base'
      default:
        return 'h-9 px-4 text-sm'
    }
  }

  const getStatusBadgeClasses = () => {
    const baseClasses = cn(
      'inline-flex items-center space-x-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
      size === 'sm' && 'px-2 py-1 text-xs',
      size === 'lg' && 'px-3 py-1 text-sm'
    )

    switch (currentStatus) {
      case 'confirmed':
        return cn(baseClasses, 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300')
      case 'declined':
        return cn(baseClasses, 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300')
      default:
        return cn(baseClasses, 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300')
    }
  }

  const getButtonClasses = (isPrimary: boolean = false) => {
    const baseClasses = cn(
      'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      getSizeClasses()
    )

    if (variant === 'compact') {
      return cn(
        baseClasses,
        isPrimary
          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
          : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
      )
    }

    return cn(
      baseClasses,
      isPrimary
        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
        : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
    )
  }

  const renderStatusBadge = () => {
    if (!showStatus) return null

    const statusIcon = currentStatus === 'confirmed' ? CheckCircle :
                      currentStatus === 'declined' ? XCircle :
                      Clock

    const Icon = statusIcon

    return (
      <div className={getStatusBadgeClasses()}>
        <Icon className="h-3 w-3" />
        {!hideLabels && (
          <span className="capitalize">
            {currentStatus === 'confirmed' && confirmText}
            {currentStatus === 'declined' && declineText}
            {currentStatus === 'pending' && pendingText}
          </span>
        )}
      </div>
    )
  }

  const renderActionButtons = () => {
    if (currentStatus === 'pending') {
      const buttons = [
        <button
          key="accept"
          type="button"
          onClick={() => handleStatusUpdate('confirmed')}
          disabled={disabled || isLoading}
          className={getButtonClasses(true)}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              {hideLabels ? null : loadingText}
            </>
          ) : (
            <>
              <CheckCircle className="mr-1 h-3 w-3" />
              {hideLabels ? null : acceptText}
            </>
          )}
        </button>,

        <button
          key="decline"
          type="button"
          onClick={() => handleStatusUpdate('declined')}
          disabled={disabled || isLoading}
          className={getButtonClasses(false)}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              {hideLabels ? null : loadingText}
            </>
          ) : (
            <>
              <XCircle className="mr-1 h-3 w-3" />
              {hideLabels ? null : 'Decline'}
            </>
          )}
        </button>
      ]

      return buttons
    }

    // Already responded - show alternative action
    const buttons = []

    if (currentStatus === 'declined') {
      buttons.push(
        <button
          key="accept-instead"
          type="button"
          onClick={() => handleStatusUpdate('confirmed')}
          disabled={disabled || isLoading}
          className={getButtonClasses(false)}
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            !hideLabels ? declineAlternativeText : <CheckCircle className="h-3 w-3" />
          )}
        </button>
      )
    }

    if (currentStatus === 'confirmed') {
      buttons.push(
        <button
          key="decline-instead"
          type="button"
          onClick={() => handleStatusUpdate('declined')}
          disabled={disabled || isLoading}
          className={getButtonClasses(false)}
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            !hideLabels ? acceptAlternativeText : <XCircle className="h-3 w-3" />
          )}
        </button>
      )
    }

    if (showResetOption && onReset) {
      buttons.push(
        <button
          key="reset"
          type="button"
          onClick={handleReset}
          disabled={disabled || isLoading}
          className={getButtonClasses(false)}
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <RotateCcw className="mr-1 h-3 w-3" />
              {hideLabels ? null : resetText}
            </>
          )}
        </button>
      )
    }

    return buttons
  }

  const buttons = renderActionButtons()

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center",
        orientation === 'vertical' ? "flex-col space-y-2" : "space-x-2",
        className
      )}
      {...props}
    >
      {renderStatusBadge()}

      <div className={cn(
        "flex",
        orientation === 'vertical' ? "flex-col space-y-1" : "space-x-1"
      )}>
        {buttons}
      </div>
    </div>
  )
})

ParticipationButton.displayName = "ParticipationButton"